import React from 'react';
import { useParams, useLocation, Link, Navigate } from 'react-router-dom';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import ContactForm from '../components/ContactForm';
import VideoFacade from '../components/VideoFacade';
import { COMPANY_INFO, CORE_SERVICES, MAIN_AREAS } from '../data/siteData';

// Map of verbatim full texts and data per service slug
const SERVICE_CONTENT = {
  'traefaeldning': {
    title: 'Træfældning udført af professionelle Arborister',
    heading: 'Professionel træfældning – Sikker og effektiv fældning af træer',
    image: 'https://assets.auxodev.dk/assets/3d990fe3-1622-47f2-aa75-fd8032eebfed/traefaeldning.jpg',
    intro: 'Et træ kan være havens stolthed, men det kan også blive en kilde til bekymring, skygge for solen eller stå i vejen for nye drømme. Når beslutningen er truffet, og træet skal væk, er en professionel og sikker træfældning den absolut vigtigste prioritet. Hærvejens Træpleje er din erfarne partner, der sikrer, at fældningen af dit træ foregår kontrolleret, effektivt og med fuld respekt for din ejendom.',
    bodyHtml: `
      <p>Vi kombinerer mange års erfaring med det nyeste sikkerhedsudstyr for at levere en service, hvor du kan være helt tryg fra start til slut.</p>
      <h2>Hvornår er træfældning den rigtige løsning?</h2>
      <p>At fælde et træ er en stor beslutning. Vi tilbyder altid ærlig og faglig rådgivning, så du er sikker på, at det er den rette løsning. Træfældning er ofte nødvendigt, når:</p>
      <ul>
        <li><strong>Træet er sygt eller dødt:</strong> Råd, svamp eller insektangreb kan svække et træ så meget, at det udgør en fare for sine omgivelser.</li>
        <li><strong>Det er stormskadet:</strong> En storm kan knække grene eller beskadige roden, hvilket gør træet ustabilt og uforudsigeligt.</li>
        <li><strong>Det skygger for meget:</strong> Et stort træ kan tage alt lyset fra haven eller terrassen og forhindre andre planter i at gro.</li>
        <li><strong>Det står i vejen:</strong> Nye byggeprojekter, en ny indkørsel eller en udvidelse af haven kan kræve, at et træ må lade livet.</li>
      </ul>
      <h2>Kontrolleret fældning med sikkerheden i top</h2>
      <p>En professionel træfældning er en nøje planlagt operation. Vi analyserer altid træets hældning, grenstruktur og de omkringliggende forhold for at bestemme den sikreste fældningsretning. Ved hjælp af præcise snit med motorsaven, fældekiler og ofte også reb, kan vi styre træets fald med centimeters præcision.</p>
      <p>I situationer, hvor der ikke er plads til at fælde træet i sin fulde længde, anvender vi i stedet topkapning. Her afmonteres træet i sektioner fra toppen og ned. Vi vælger altid den metode, der er mest sikker og effektiv for netop din opgave.</p>
      <h2>Fuld servicepakke – vi klarer det hele</h2>
      <p>Når træet ligger ned, er vores arbejde ikke nødvendigvis slut. Vi tilbyder en komplet løsning, der efterlader din have pæn og klar til brug:</p>
      <ul>
        <li><strong>Flishugning:</strong> Vi kan omdanne grene og kvas til flis, som du kan bruge i havens bede.</li>
        <li><strong>Oprydning:</strong> Vi rydder altid pænt op efter os.</li>
        <li><strong>Stubfræsning:</strong> Vi kan fjerne den tilbageværende stub med en effektiv rodfræser.</li>
      </ul>
    `,
    localPageSlugSuffix: 'traefaeldning-i-',
    relatedServices: ['topkapning', 'stubfraesning', 'beskaering-af-traeer'],
  },

  'topkapning': {
    title: 'Professionel Topkapning',
    heading: 'Topkapning – Kirurgisk præcision, når pladsen er trang',
    image: 'https://assets.auxodev.dk/assets/3d990fe3-1622-47f2-aa75-fd8032eebfed/topkapning-i-kolding.jpg',
    intro: 'Når et træ skal fjernes, men haven er lille, baggården er lukket, eller træet står klos op ad huset, er en almindelig træfældning ikke en mulighed. Her kræves en specialiseret løsning, der kombinerer klatreteknik med kirurgisk præcision. Den løsning er topkapning – den sikreste og mest kontrollerede metode til at fjerne træer på steder, hvor pladsen er en begrænsning.',
    bodyHtml: `
      <p>Hærvejens Træpleje er et hold af erfarne og certificerede arborister, der har specialiseret sig i at udføre selv de mest komplicerede topkapninger. Vi omdanner en potentielt risikabel opgave til en sikker og kontrolleret operation, der skåner din ejendom og dine omgivelser.</p>
      <h2>Hvad er forskellen på topkapning og træfældning?</h2>
      <p>Forskellen ligger i metoden og kontrollen. Ved en traditionel træfældning saves træet over ved roden og fældes i sin fulde længde. Det kræver et frit areal, der mindst svarer til træets højde. Ved topkapning er pladskravet minimalt.</p>
      <p>Vores klatrende arborist arbejder sig op i træet og afmonterer det systematisk fra toppen og ned. Grene og stammestykker saves af i mindre, håndterbare sektioner. Hver enkelt del bliver enten kastet med præcision ned på et lille, aftalt område eller – hvis det er nødvendigt – hejst kontrolleret ned med reb. Dette giver os 100% kontrol over hele processen og sikrer, at hverken dit drivhus, din terrasse eller naboens garage kommer til skade.</p>
      <h2>Hvorfor vælge en certificeret arborist?</h2>
      <p>Topkapning er en af de mest teknisk krævende discipliner inden for træpleje. Det er ikke nok at have en motorsav og mod på at klatre. Det kræver dybdegående viden om rebsystemer, ankerpunkter, træets biologi og de kræfter, der er på spil. Vores arborister er certificerede, hvilket er din garanti for, at vi altid anvender de sikreste og mest anerkendte metoder i branchen.</p>
      <p>Vi er desuden fuldt forsikrede, så du kan være helt tryg fra start til slut. Vi behandler din ejendom med respekt, rydder pænt op efter os og forlader ikke opgaven, før du er fuldt ud tilfreds.</p>
    `,
    relatedServices: ['traefaeldning', 'stubfraesning', 'beskaering-af-traeer'],
  },

  'stubfraesning': {
    title: 'Rodfræsning - Hærvejens Træpleje',
    heading: 'Stubfræsning & Rodfræsning: Gør haven klar til nye drømme',
    image: 'https://assets.auxodev.dk/assets/3d990fe3-1622-47f2-aa75-fd8032eebfed/stubfraesning.jpg',
    intro: 'Træet er fældet, og lyset er vendt tilbage til haven. Men tilbage står den sidste, genstridige rest: træstubben. Den er ikke bare en torn i øjet på en ellers pæn græsplæne; den er en forhindring for nye haveprojekter, en snublerisiko for legende børn og et levn fra fortiden, der står i vejen for fremtiden. Løsningen er stubfræsning – også kendt som rodfræsning – den mest effektive og skånsomme metode til at fjerne en træstub.',
    bodyHtml: `
      <p>Hærvejens Træpleje er specialister i at omdanne problematiske træstubbe til brugbar havejord. Med vores moderne maskinpark og erfarne mandskab fjerner vi hurtigt den sidste rest af træet, så du kan komme videre med dine planer.</p>
      <h2>Hvorfor vælge fræsning frem for opgravning?</h2>
      <p>At grave en træstub op med håndkraft eller en minigraver er en voldsom proces. Det efterlader et stort, dybt hul, ødelægger den omkringliggende græsplæne og kræver bortskaffelse af en tung og uhåndterlig stub. Stubfræsning er en langt mere elegant og kirurgisk løsning.</p>
      <p>Vores specialiserede stubfræser er udstyret med et hurtigt roterende fræsehjul, der simpelthen pulveriserer stubben og de øverste rødder ned til en dybde på ca. 30 cm under jordniveau. Processen omdanner stubben til en blanding af jord og små træflis. Hullet kan derefter nemt fyldes op, og du kan så græs direkte ovenpå. Fordelene er klare:</p>
      <ul>
        <li><strong>Minimal skade på haven:</strong> Ingen dybe spor fra tunge maskiner.</li>
        <li><strong>Hurtigt og effektivt:</strong> En standard træstub er typisk fjernet på under en time.</li>
        <li><strong>Klar til nye projekter:</strong> Jorden er umiddelbart klar til en ny græsplæne, et bed eller fliselægning.</li>
        <li><strong>Bæredygtigt:</strong> Den næringsrige træflis kan med fordel genbruges som topdække i havens bede.</li>
      </ul>
      <h2>Få et samlet tilbud og spar penge</h2>
      <p>Står du overfor at skulle have fældet et træ? Så kan det næsten altid betale sig at få et samlet tilbud på både træfældning og den efterfølgende stubfræsning. Når vi alligevel er på adressen med mandskab og maskiner, kan vi udføre begge opgaver mere effektivt. Den besparelse giver vi videre til dig i form af en skarpere samlet pris.</p>
    `,
    relatedServices: ['traefaeldning', 'topkapning'],
  },

  'beskaering-af-traeer': {
    title: 'Træbeskæringsservice i Vejle - Kolding Træbeskæring',
    heading: 'Beskæring af træer – sund vækst, sikkerhed og form',
    image: 'https://assets.auxodev.dk/assets/3d990fe3-1622-47f2-aa75-fd8032eebfed/imageye-imgi-35-dsc00451-251.jpg',
    intro: 'Hos Hærvejens Træpleje har vi både ekspertisen og erfaringen til at varetage mange forskellige typer opgaver for vores kunder i forbindelse med beskæring af træer og buske samt træfældning og topkapning. Vi tilbyder både beskæring af æbletræer, pæretræer og andre frugttræer samt mange andre forskellige typer træer.',
    bodyHtml: `
      <p>Vi varetager typisk træbeskæring for erhvervskunder og private husejere i Vejle, Fredericia og Kolding, men vi tilbyder også beskæring af træer flere andre steder.</p>
      <h3>Vi tilbyder blandt andet:</h3>
      <ul>
        <li>Beskæring af æbletræer</li>
        <li>Beskæring af pæretræer</li>
        <li>Beskæring af blommetræer</li>
        <li>Beskæring af andre frugttræer</li>
        <li>Beskæring af løvfældende træer</li>
        <li>Beskæring af nåletræer</li>
        <li>Andre former for træbeskæring.</li>
      </ul>
      <p>Vi tilbyder nogle af markedets laveste og mest konkurrencedygtige priser på træbeskæring, og vi står til rådighed med kompetent rådgivning. Ønsker du en pris på beskæring af træer, er du velkommen til at kontakte os for at indhente et uforpligtende tilbud.</p>
      <h2>Vi mestrer forskellige beskæringsteknikker</h2>
      <h3>Kroneløft</h3>
      <p>Et kroneløft er, hvor man løfter kronen på træet ved at tage de nederste grene af. Det gør man oftest langs veje og fortove, da der er regler og love om, at de ikke må hænge for lavt.</p>
      <h3>Kronetynding</h3>
      <p>Kronetynding er, hvor man beholder formen og størrelsen på træet. Derefter tager man en masse små mellemgrene, som ingen betydning har for træets form. Man tager ca. 15-20 %, så der kan komme en masse lys og luft igennem.</p>
      <h3>Kronereducering</h3>
      <p>Kronereducering er, når man har et træ, som er blevet alt for stort. Derfor skærer man det tilbage og lader det skyde på ny. På den måde kan man beholde sine store træer over længere tid.</p>
      <h2>Beskæring af træer – hvornår?</h2>
      <p>Der kan være stor forskel på, hvornår træer bør beskæres. Hvilket tidspunkt og hvilken årstid der er optimal afhænger blandt andet af, hvad formålet med træbeskæringen er. Nogle træer har stor gavn af at blive beskåret om sommeren, mens andre med fordel kan beskæres enten forår, efterår eller vinter.</p>
      <h2>Beskæring af æbletræer – hvornår?</h2>
      <p>Hvornår beskæring af æbletræer bør udføres afhænger ligeledes af, hvad formålet med beskæringen er, og hvor gammelt træet er. Det gør sig i det hele taget gældende i forbindelse med beskæring af frugttræer generelt. Beskæring af gamle æbletræer kan udføres, når træet er blevet så stort, at du ikke kan nå æblerne. Beskæring af nye æbletræer bør ikke være kraftig. Typisk kan du beskære træet med en tredjedel af dets højde om året.</p>
    `,
    relatedServices: ['traefaeldning', 'topkapning'],
  },

  'anlaeg': {
    title: 'Anlæg - Hærvejens Træpleje',
    heading: 'Bor du tæt på jernbanen? Dit træ kan koste dig dyrt',
    image: 'https://assets.auxodev.dk/assets/3d990fe3-1622-47f2-aa75-fd8032eebfed/helsingor-bane.jpg',
    intro: 'Hvert år vælter hundredvis af træer ned over jernbanen i Danmark. Alene i 2023 og 2024 var det over 550 træer, og næsten halvdelen kom fra private grunde. Problemet? Hvis dit træ vælter og rammer skinnerne, hæfter du som ejer. Ikke kun for skader på tog og køreledninger, men også for forsinkelser og oprydning. Det kan hurtigt løbe op i sekscifrede beløb.',
    bodyHtml: `
      <h2>Hvornår er du ansvarlig?</h2>
      <p>Ifølge Jernbaneloven kan Banedanmark kræve træer fjernet på ejerens bekostning, hvis de udgør en risiko for togdriften. Som nabo til banen har du pligt til at holde øje med dine træer og fjerne dem, inden de forvolder skade.</p>
      <p>Står der køreledninger langs din strækning, gælder der særlige regler. Elektrificering af jernbanen betyder nye servitutter for mange grundejere, og kravene til beplantning skærpes.</p>
      <h2>Sådan spotter du et risikotræ</h2>
      <p>Banedanmark opfordrer naboer til at give deres træer et sundhedstjek. Kig efter:</p>
      <ul>
        <li>Synligt råd eller svampe på stammen</li>
        <li>Døde eller hængende grene i kronen</li>
        <li>Skæv vækst eller hældning mod banen</li>
        <li>Revner i stammen eller rodhalsen</li>
        <li>Løftet jord omkring rødderne</li>
      </ul>
      <p>Er du i tvivl, bør du få en fagperson til at vurdere træet. Et sundt træ der vælter i storm betragtes som force majeure. Men var træet svækket, og du burde have opdaget det, står du med ansvaret.</p>
      <h2>Vi hjælper naboer til jernbanen</h2>
      <p>Hos Hærvejens Træpleje har vi erfaring med træfældning i vanskelige omgivelser, herunder tæt på jernbaner, bygninger og elledninger. Vi vurderer dine træer og rådgiver om, hvad der bør fældes eller beskæres. Skal der fældes inden for fire meter fra skinnerne, kræver det tilladelse fra Banedanmark. Vi kender reglerne og sørger for, at alt foregår korrekt og sikkert.</p>
    `,
    relatedServices: ['traefaeldning', 'topkapning'],
  },

  'haekklipning': {
    title: 'Effektiv Hækkeklipning til Private & Erhverv - Hærvejens Træpleje',
    heading: 'Hækklipning efter dine ønsker – få professionel hjælp til haven',
    image: 'https://assets.auxodev.dk/assets/3d990fe3-1622-47f2-aa75-fd8032eebfed/haek-haervejens-traepleje-117-300x300.jpg',
    intro: 'Velkommen til Hærvejens Træpleje, hvor vi tilbyder hækklipning for private såvel som virksomheder. Vi er din oplagte samarbejdspartner, når du har brug for professionel træpleje – vi har nemlig mange års erfaring i branchen, og faglig ekspertise med en grundig kunderådgivning er bare én af vores mange mærkesager.',
    bodyHtml: `
      <p>Vores kunder befinder sig typisk i området omkring Fredericia, Kolding og Vejle, men har du base et andet sted, er det ingen hindring for os.</p>
      <h2>Vi har udstyret til at klare små som store opgaver</h2>
      <p>Hos Hærvejens Træpleje finder du et hold af veluddannede gartnere, som tilbyder klipning af dit hjem eller dit firmas grønne områder og udearealer. Vores hækklippere er udstyret med teleskopstænger, der kan nå høje hække og buske fra jorden. Vi har dermed både udstyret og kompetencerne til at klare små som store opgaver, uanset om du har behov for beskæring af buske eller klipning af en hæk. Kunne du tænke dig en højdereducering af din hæk, så er det også muligt.</p>
      <p>Vi anbefaler, at du kontakter en professionel, når det er tid til en hækklipning. Så er du sikker på, at arbejdet bliver udført ordentligt helt fra starten, og at du få et resultat, der lever op til dine forventninger.</p>
      <h2>Hvad koster hækklipning?</h2>
      <p>Den normale pris på hækklipning er 35 kr. pr. meter plus moms. Dette gælder for 1. side og top, og kun hvis hækken er under 1,6 meter. Herefter kommer der ekstra gebyr, hvis hækken er højere.</p>
      <p>Kontakt os allerede i dag og få et godt tilbud på klipning eller beskæring af hæk – samt generel vedligeholdelse af store, grønne udearealer, for eksempel andelsboligforeningens fællesareal.</p>
    `,
    relatedServices: ['beskaering-af-traeer', 'traefaeldning'],
  },

  'alm-skovning': {
    title: 'Alm. skovning - Hærvejens Træpleje',
    heading: 'Alm. skovning – Håndskovning og tynding',
    image: 'https://assets.auxodev.dk/assets/3d990fe3-1622-47f2-aa75-fd8032eebfed/akm-skov1-180.jpg',
    intro: 'Vi tilbyder også almindlig håndskovning. Har du et mindre eller stører skov stykke, som trænger til at blive tyndet i? eller går du meget på jagt, og kunne du godt tænke dig at få skovet nogle spor? så ring til os på 27 13 57 87 vi står altid klar til at hjælpe.',
    bodyHtml: `
      <h2>Hånd skovning</h2>
      <p>Vi tilbyder også almindlig håndskovning. Har du et mindre eller stører skov stykke, som trænger til at blive tyndet i? eller går du meget på jagt, og kunne du godt tænke dig at få skovet nogle spor? så ring til os på <strong>27 13 57 87</strong> vi står altid klar til at hjælpe.</p>
    `,
    videoId: 'hpPCJ6g0S5s',
    videoTitle: 'Fældning af en meget stor og rådnet poppel🏡🪵✅ - Hærvejens Træpleje',
    relatedServices: ['traefaeldning', 'anlaeg', 'stubfraesning'],
  },
};

export default function ServiceDetailPage({ forcedSlug }) {
  const { slug } = useParams();
  const location = useLocation();

  // Determine current slug either from prop, param, or pathname
  const currentSlug = forcedSlug || slug || location.pathname.replace(/^\//, '').replace(/\/$/, '');
  const normalizedSlug = currentSlug === 'beskaering' ? 'beskaering-af-traeer' : currentSlug === 'rodfraesning' ? 'stubfraesning' : currentSlug;

  const serviceMeta = CORE_SERVICES.find((s) => s.slug === normalizedSlug || s.altSlug === normalizedSlug);
  const content = SERVICE_CONTENT[normalizedSlug];

  if (!serviceMeta || !content) {
    return <Navigate to="/services" replace />;
  }

  return (
    <>
      <SEO
        title={serviceMeta.seoTitle || `${serviceMeta.title} - Hærvejens Træpleje`}
        description={serviceMeta.metaDesc || serviceMeta.intro}
        ogImage={serviceMeta.image}
        canonical={`https://xn--hrvejenstrpleje-xlbj.dk/${serviceMeta.slug}/`}
      />

      <Breadcrumbs
        items={[
          { label: 'Ydelser', href: '/services' },
          { label: serviceMeta.title },
        ]}
      />

      {/* Hero Header */}
      <section className="bg-[#10271c] text-white py-14 sm:py-20 px-4 sm:px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={content.image} alt={serviceMeta.title} className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <span className="text-xs font-bold text-[#7dd2a6] bg-[#1a3d2c] px-3.5 py-1.5 rounded-full inline-block mb-4">
            Professionel træpleje
          </span>
          <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-4">
            {content.heading || serviceMeta.title}
          </h1>
          <p className="text-base sm:text-lg text-[#cadcd1] leading-relaxed max-w-3xl mx-auto">
            {content.intro}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-14 sm:py-20 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Main prose */}
          <div className="lg:col-span-8 space-y-8">
            <div
              className="article-prose bg-white p-6 sm:p-10 rounded-2xl border border-[#dce5df] shadow-xs"
              dangerouslySetInnerHTML={{ __html: content.bodyHtml }}
            />

            {content.videoId && (
              <div className="space-y-3">
                <h3 className="text-xl font-bold text-[#14291d]">
                  Se vores arbejde i praksis
                </h3>
                <VideoFacade
                  title={content.videoTitle || 'Se video'}
                  videoId={content.videoId}
                  provider="youtube"
                />
              </div>
            )}

            {/* Geographical coverage box */}
            <div className="bg-[#f2f7f4] rounded-2xl p-6 sm:p-8 border border-[#cfe0d6]">
              <h3 className="text-xl font-bold text-[#14291d] mb-3 flex items-center gap-2">
                <i className="fa-solid fa-location-dot text-[#2d6a4f]" aria-hidden="true" />
                <span>Vi udfører {serviceMeta.shortTitle.toLowerCase()} i hele Trekantområdet & Østjylland</span>
              </h3>
              <p className="text-sm text-[#46594e] mb-5">
                Uanset hvor du bor, er vores erfarne hold af arborister klar til at rykke ud. Læs mere om vores lokale landingssider i de enkelte byer:
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {MAIN_AREAS.map((area) => {
                  // Find related local page if exists
                  const targetSlug =
                    serviceMeta.slug === 'traefaeldning'
                      ? `traefaeldning-i-${area.slug}`
                      : serviceMeta.slug === 'topkapning'
                      ? (area.slug === 'aarhus' ? 'topkapning-aarhus' : `topkapning-${area.slug}`)
                      : serviceMeta.slug === 'stubfraesning'
                      ? (area.slug === 'aarhus' ? 'rodfraesning-aarhus' : `rodfraesning-i-${area.slug}`)
                      : area.slug;

                  return (
                    <Link
                      key={area.slug}
                      to={`/${targetSlug}`}
                      className="px-3.5 py-2.5 rounded-xl bg-white border border-[#d0ded5] text-xs sm:text-sm font-semibold text-[#1a2c20] hover:bg-[#1b4332] hover:text-white hover:border-[#1b4332] transition-colors flex items-center justify-between"
                    >
                      <span>{serviceMeta.shortTitle} i {area.name}</span>
                      <i className="fa-solid fa-chevron-right text-[10px]" aria-hidden="true" />
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-6">
            {/* Quick quote / phone card */}
            <div className="bg-[#143627] text-white rounded-2xl p-6 shadow-sm">
              <span className="text-xs font-semibold text-[#7dd2a6]">
                Direkte kontakt
              </span>
              <h3 className="text-xl font-bold mt-1 mb-3">Brug for et uforpligtende tilbud?</h3>
              <p className="text-sm text-[#c8ded1] mb-6 leading-relaxed">
                Ring til os direkte eller få en gratis besigtigelse af dit træ. Vi giver altid en fast pris uden skjulte gebyrer.
              </p>
              <a
                href={`tel:${COMPANY_INFO.phoneRaw}`}
                className="w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl bg-white text-[#122b1f] font-bold text-sm hover:bg-[#f2f7f4] transition-colors whitespace-nowrap mb-3"
              >
                <i className="fa-solid fa-phone text-[#2d6a4f]" aria-hidden="true" />
                <span className="whitespace-nowrap">Ring {COMPANY_INFO.phone}</span>
              </a>
              <Link
                to="/kontakt"
                className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl border border-white/30 text-white font-semibold text-sm hover:bg-white/10 transition-colors"
              >
                <span>Udfyld kontaktformular »</span>
              </Link>
            </div>

            {/* Related Services */}
            <div className="bg-white rounded-2xl p-6 border border-[#dce5df] shadow-xs">
              <h4 className="text-base font-bold text-[#14291d] mb-4">Relaterede ydelser</h4>
              <ul className="space-y-2.5">
                {CORE_SERVICES.filter((s) => s.slug !== serviceMeta.slug).map((s) => (
                  <li key={s.slug}>
                    <Link
                      to={`/${s.slug}`}
                      className="flex items-center gap-3 p-2 rounded-xl hover:bg-[#f4f7f5] text-sm text-[#273a2f] transition-colors"
                    >
                      <div className="w-8 h-8 rounded-lg bg-[#e8f1ec] text-[#1b4332] flex items-center justify-center text-xs shrink-0">
                        <i className={`fa-solid ${s.icon}`} aria-hidden="true" />
                      </div>
                      <span className="font-medium">{s.title}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Certifications Box */}
            <div className="bg-[#faf9f5] rounded-2xl p-6 border border-[#e4ded0] text-xs text-[#4b5950] space-y-3">
              <div className="flex items-center gap-2 font-bold text-[#183124] text-sm">
                <i className="fa-solid fa-shield-check text-[#2d6a4f]" aria-hidden="true" />
                <span>Din tryghed på opgaven</span>
              </div>
              <p>• Uddannet ved Skovskolen, Københavns Universitet</p>
              <p>• Forsikret for op til 10 mio. kr. hos Topdanmark</p>
              <p>• Arbejdstilsynets regler for sikker klatring overholdes altid</p>
              <p>• Vi forlader ikke din have, før den er pæn og ryddelig</p>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
